import Phaser from "phaser";
import lottie, { AnimationItem } from "lottie-web";
import { GameBox } from "../game/GameBox";

type GameMode = "score" | "free";

export default class UIScene extends Phaser.Scene {
  private logoImage!: Phaser.GameObjects.Image;
  private movesText!: Phaser.GameObjects.Text;
  private timeText!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private startButton!: Phaser.GameObjects.Container;
  private startArrow!: Phaser.GameObjects.Text;
  private startLabel!: Phaser.GameObjects.Text;
  private arrowTween?: Phaser.Tweens.Tween;
  private muteText!: Phaser.GameObjects.Text;
  private pauseText!: Phaser.GameObjects.Text;
  private previewText!: Phaser.GameObjects.Text;
  private debugText!: Phaser.GameObjects.Text;
  private testModalText!: Phaser.GameObjects.Text;
  private versionText!: Phaser.GameObjects.Text;
  private freeModalTestText!: Phaser.GameObjects.Text;
  private time3SecText!: Phaser.GameObjects.Text;
  private congratsContainer?: Phaser.GameObjects.Container;
  private modeSelectContainer?: Phaser.GameObjects.Container;
  private lottieContainer?: HTMLDivElement;
  private lottieAnim?: AnimationItem;
  private bound = false;
  private gameStarted = false;
  private nextStageButton?: Phaser.GameObjects.Text;
  private api?: {
    setMuted: (v: boolean) => void;
    getMuted: () => boolean;
    pause: () => void;
    resume: () => void;
    restart: () => void;
    solve: () => void;
    setToLastStage: () => void;
    start: () => void;
    nextStage: () => void;
    selectMode: (mode: GameMode) => void;
    selectPuzzleSize: (size: 2 | 3 | 4 | 5 | 6) => void;
    getGameMode: () => GameMode;
    togglePreview: () => void;
    getCompletedSizes: () => (2 | 3 | 4 | 5 | 6)[];
    hasSavedData: () => boolean;
    checkSavedData: (callback: (hasData: boolean) => void) => void;
    continueFromSaved: () => void;
    saveAndExit: () => void;
    saveProgressAndEnd: (onSaved: () => void) => void;
    setTimeTo3Seconds: () => void;
  };
  private helpModalContainer?: Phaser.GameObjects.Container;
  private loadingContainer?: Phaser.GameObjects.Container;
  private puzzleSizeSelectContainer?: Phaser.GameObjects.Container;
  private lastTotalScore?: number;
  private isLastStage?: boolean;
  private isTimeUp?: boolean;

  constructor() {
    super("UIScene");
  }

  create() {
    console.log("[UIScene] create() メソッドが実行されました");
    const base = this.game.registry.get("defaultTextStyle") || {
      fontFamily: "KirangHaerang",
      fontSize: "48px",
      color: "#ffffff",
    };

    const w = this.scale.width;
    const h = this.scale.height;
    const pad = 24;
    console.log("[UIScene] 画面サイズ:", { width: w, height: h, pad });

    // ロゴ画像（Top画面では中央に配置）
    this.logoImage = this.add
      .image(w / 2, h * 0.38, "logo")
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0)
      .setDepth(100);
    
    // ロゴのサイズを適切に調整（画面幅の60%程度、縦横比を保持）
    const logoMaxWidth = w * 0.75;
    this.updateLogoSize(logoMaxWidth);

    this.movesText = this.add.text(pad, pad, "MOVES 0", { ...base, fontSize: "48px" }).setScrollFactor(0).setDepth(100);
    this.timeText = this.add.text(pad, pad + 50, "TIME 0.00", { ...base, fontSize: "48px" }).setScrollFactor(0).setDepth(100);
    this.scoreText = this.add.text(pad, pad + 100, "", { ...base, fontSize: "48px", color: "#ffdd00" }).setScrollFactor(0).setDepth(100);
    this.statusText = this.add
      .text(pad, pad + 150, "", { ...base, fontSize: "48px" })
      .setScrollFactor(0)
      .setDepth(100);

    // Startボタン（画面下部・中央寄せ）
    this.startArrow = this.add.text(-90, 0, "▶", { ...base, fontSize: "64px", color: "#00ff88" }).setOrigin(0.5, 0.5);
    this.startLabel = this.add.text(20, 0, "START", { ...base, fontSize: "64px", color: "#00ff88" }).setOrigin(0.5, 0.5);
    this.startButton = this.add.container(w / 2, h * 0.89, [this.startArrow, this.startLabel]);
    this.startButton.setSize(250, 70);
    this.startButton.setScrollFactor(0);
    this.startButton.setDepth(100);
    this.startButton.setInteractive({ useHandCursor: true });
    this.startButton.setVisible(false);

    // ▶を点滅させる
    this.arrowTween = this.tweens.add({
      targets: this.startArrow,
      alpha: 0.2,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    this.muteText = this.add
      .text(w - pad, pad, "MUTE: OFF", { ...base, fontSize: "48px" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true });

    this.pauseText = this.add
      .text(w - pad, pad + 50, "PAUSE", { ...base, fontSize: "48px", color: "#666666" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true });

    this.previewText = this.add
      .text(w - pad, pad + 100, "PREVIEW", { ...base, fontSize: "48px", color: "#666666" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true });


    this.debugText = this.add
      .text(w - pad, pad + 250, "DEBUG", { ...base, fontSize: "48px", color: "#666666" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setVisible(false) // 非アクティブ
      .setInteractive({ useHandCursor: false }); // 無効化

    // Free Modal Testボタン（デバッグ用）- 非アクティブ
    this.freeModalTestText = this.add
      .text(w - pad, pad + 300, "FREE MODAL", { ...base, fontSize: "32px", color: "#666666" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setVisible(false)
      .setInteractive({ useHandCursor: false });

    // Time 3秒ボタン（デバッグ用）- 非アクティブ
    this.time3SecText = this.add
      .text(w - pad, pad + 350, "TIME 3s", { ...base, fontSize: "32px", color: "#666666" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setVisible(false) // 非アクティブ
      .setInteractive({ useHandCursor: false }); // 無効化
    this.time3SecText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      if (!this.api || !this.gameStarted) return;
      this.playButtonSfx();
      this.api?.setTimeTo3Seconds();
    });

    // Saveボタン（Free Mode用）
    const saveText = this.add
      .text(w - pad, pad + 150, "SAVE", { ...base, fontSize: "48px", color: "#666666" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });
    saveText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      // 【FREE MODE非アクティブ化】FREE MODEの条件チェックをコメントアウト
      // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
      // 復元時は以下のコメントを解除
      /*
      if (!this.api || !this.gameStarted || this.api.getGameMode() !== "free") return;
      this.playButtonSfx();
      // SAVEをタップしたらTIMEを停止
      this.api.pause();
      this.showSaveConfirm(() => {
        // "はい"でタイムをリセットし、Stage進捗もリセットし非表示に
        // Saveボタンを非表示にする
        const saveText = (this as any).saveText;
        if (saveText) {
          saveText.setVisible(false);
          saveText.setColor("#666666");
          saveText.removeInteractive();
        }
        this.api?.saveAndExit();
      });
      */
      // FREE MODE非アクティブ化により処理をスキップ
      return;
    });
    (this as any).saveText = saveText;

    // テスト用モーダル表示ボタン（開発環境用）- 画面下部に配置（非アクティブ）
    this.testModalText = this.add
      .text(w - pad, h - pad - 60, "TEST MODAL", { ...base, fontSize: "48px", color: "#666666" })
      .setOrigin(1, 1)
      .setScrollFactor(0)
      .setDepth(100)
      .setVisible(false) // 非表示
      .setInteractive({ useHandCursor: false }); // 無効化

    // バージョン表記（画面右下）- 非表示
    const buildDate = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const versionString = `v1.0.0 (${buildDate})`;
    console.log("[UIScene] バージョン表記を作成:", versionString);
    this.versionText = this.add
      .text(w - pad, h - pad, versionString, {
        fontFamily: "KirangHaerang",
        fontSize: "24px",
        color: "#999999",
      })
      .setOrigin(1, 1)
      .setScrollFactor(0)
      .setDepth(1000)
      .setAlpha(0.8)
      .setVisible(false); // 非表示
    console.log("[UIScene] バージョン表記の位置:", { x: w - pad, y: h - pad, visible: this.versionText.visible, alpha: this.versionText.alpha, depth: this.versionText.depth });

    this.scale.on(Phaser.Scale.Events.RESIZE, (gameSize: Phaser.Structs.Size) => {
      const ww = gameSize.width;
      const hh = gameSize.height;
      // ロゴはモード選択画面では中央に
      if (this.modeSelectContainer) {
        this.logoImage.setPosition(ww / 2, hh * 0.38);
        const logoMaxWidth = ww * 0.6;
        this.updateLogoSize(logoMaxWidth);
      }
      this.startButton.setPosition(ww / 2, hh * 0.89);
      this.muteText.setX(ww - pad);
      this.pauseText.setX(ww - pad);
      this.debugText.setX(ww - pad);
      this.freeModalTestText.setX(ww - pad);
      if (this.time3SecText) {
        this.time3SecText.setX(ww - pad);
      }
      this.testModalText.setPosition(ww - pad, hh - pad - 60);
      this.versionText.setPosition(ww - pad, hh - pad);
      
      // Lottieコンテナの位置とサイズを更新（表示中の場合）
      this.updateLottieContainerPosition();
    });

    this.events.on(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.arrowTween?.stop();
    });

    this.events.on("bindGame", (api: any) => {
      this.api = api;
      if (!this.bound) this.bindButtons();
      this.refreshMuteLabel();
    });

    // Saveボタンを非表示にするイベント
    this.events.on("hideSaveButton", () => {
      const saveText = (this as any).saveText;
      if (saveText) {
        saveText.setVisible(false);
        saveText.setColor("#666666");
        saveText.removeInteractive();
      }
    });

    const gameScene = this.scene.get("GameScene");
    if (gameScene) {
      gameScene.events.emit("uiReady");
    }

    this.events.on("tick", (p: { moves: number; elapsedMs: number; remainingMs: number; totalScore: number; gameMode: GameMode }) => {
      this.movesText.setText(`MOVES ${p.moves}`);
      if (p.gameMode === "score") {
        const remainSec = Math.max(0, p.remainingMs / 1000);
        this.timeText.setText(`TIME ${remainSec.toFixed(1)}s`);
        // 残り30秒以下で赤色
        this.timeText.setColor(remainSec <= 30 ? "#ff4444" : "#ffffff");
        this.scoreText.setText(`SCORE ${p.totalScore}`);
      } else {
        this.timeText.setText(`TIME ${(p.elapsedMs / 1000).toFixed(2)}`);
        this.timeText.setColor("#ffffff");
        this.scoreText.setText("");
      }
      // 【FREE MODE非アクティブ化】Saveボタンの表示/非表示を制御（Free Modeのみ）
      // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
      // 復元時は以下のコメントを解除
      /*
      // ただし、hideSaveButtonイベントで非表示にされた場合は再表示しない
      const saveText = (this as any).saveText;
      if (saveText) {
        // 既に非表示になっている場合は、tickイベントで再表示しない
        if (!saveText.visible) {
          // 非表示のまま維持（ただし、Time 3秒ボタンの処理は続行）
        } else if (p.gameMode === "free" && this.gameStarted) {
          saveText.setVisible(true);
          saveText.setColor("#ffffff");
          saveText.setInteractive({ useHandCursor: true });
        } else {
          saveText.setVisible(false);
          saveText.setColor("#666666");
          saveText.removeInteractive();
        }
      }
      */
      // Time 3秒ボタンは非アクティブ（削除はしない）
      // if (this.time3SecText) {
      //   if (p.gameMode === "score" && this.gameStarted) {
      //     this.time3SecText.setVisible(true);
      //     this.time3SecText.setColor("#ffaa00");
      //     this.time3SecText.setInteractive({ useHandCursor: true });
      //   } else {
      //     this.time3SecText.setVisible(false);
      //     this.time3SecText.setColor("#666666");
      //     this.time3SecText.removeInteractive();
      //   }
      // }
    });

    this.events.on("setStatus", (p: { 
      status: string;
      currentStage?: number;
      totalStages?: number;
      clearedCount?: number;
      gameMode?: GameMode;
    }) => {
      if (p.status === "") {
        // 空文字列の場合は非表示にする
        this.statusText.setText("");
        this.statusText.setVisible(false);
      } else {
        this.statusText.setText(p.status);
        this.statusText.setVisible(true);
      }
    });

    this.events.on("showModeSelect", () => {
      this.showModeSelect();
    });

    this.events.on("showLoading", () => {
      this.showLoading();
    });

    this.events.on("updateLoadingProgress", (progress: number) => {
      this.updateLoadingProgress(progress);
    });

    this.events.on("hideLoading", () => {
      this.hideLoading();
    });

    this.events.on("showStartButton", () => {
      this.startButton.setVisible(true);
      this.startArrow.setAlpha(1);
      this.arrowTween?.resume();
    });

    this.events.on("showCongrats", (p: {
      time: number;
      moves: number;
      stage?: number;
      isLast?: boolean;
      isTimeUp?: boolean;
      stageScore?: number;
      totalScore?: number;
      correctTiles?: number;
      gameMode?: GameMode;
    }) => {
      this.showCongratulations(p);
    });

    this.events.on("hideCongrats", () => {
      this.hideCongratulations();
    });
  }

  private showModeSelect() {
    this.hideModeSelect();
    this.hidePuzzleSizeSelect();
    this.hideCongratulations();
    this.startButton.setVisible(false);
    this.gameStarted = false;
    this.pauseText.setColor("#666666");
    this.pauseText.removeInteractive();
    this.previewText.setColor("#666666");
    this.previewText.removeInteractive();
    this.debugText.setColor("#666666");

    // ロゴを中央に配置して表示
    const w = this.scale.width;
    const h = this.scale.height;
    this.logoImage.setPosition(w / 2, h * 0.38);
    this.logoImage.setVisible(true);
    const logoMaxWidth = w * 0.75;
    this.updateLogoSize(logoMaxWidth);

    const base = this.game.registry.get("defaultTextStyle") || {
      fontFamily: "KirangHaerang",
      fontSize: "48px",
      color: "#ffffff",
    };

    const iconSize = 48;
    const iconGap = 16; // アイコンとテキストの間隔

    // スコアモードボタン（アイコン + テキスト）- 全体を中央寄せ
    const scoreLabel = this.add.text(0, 0, "GAME PLAY", {
      ...base,
      fontSize: "56px",
      color: "#ff8800",
    }).setOrigin(0, 0.5);
    const scoreTotalWidth = iconSize + iconGap + scoreLabel.width;
    const scoreStartX = -scoreTotalWidth / 2;
    const scoreIconX = scoreStartX + iconSize / 2;
    const scoreTextX = scoreStartX + iconSize + iconGap;
    const scoreIcon = this.add.image(scoreIconX, 0, "iconStopWatch").setDisplaySize(iconSize, iconSize);
    scoreLabel.setX(scoreTextX);
    const scoreBtnContainer = this.add.container(w / 2, h * 0.52, [scoreIcon, scoreLabel]);
    scoreBtnContainer.setSize(scoreTotalWidth, 60);
    scoreBtnContainer.setInteractive({ useHandCursor: true });
    scoreBtnContainer.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.onModeSelected("score");
    });

    // 【FREE MODE非アクティブ化】フリーモードボタン（アイコン + テキスト）- SCORE MODEと位置を揃える
    // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
    const freeIcon = this.add.image(scoreIconX, 0, "iconGame").setDisplaySize(iconSize, iconSize);
    const freeLabel = this.add.text(scoreTextX, 0, "FREE MODE", {
      ...base,
      fontSize: "56px",
      color: "#00aaff",
    }).setOrigin(0, 0.5);
    const freeBtnContainer = this.add.container(w / 2, h * 0.58, [freeIcon, freeLabel]);
    freeBtnContainer.setSize(scoreTotalWidth, 60);
    freeBtnContainer.setInteractive({ useHandCursor: true });
    freeBtnContainer.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.onModeSelected("free");
    });
    // FREE MODEボタンを非表示にする（復元時はこの行を削除）
    freeBtnContainer.setVisible(false);
    freeBtnContainer.removeInteractive();

    // 遊び方ボタン（Score Modeとの行間を適切に調整）
    const helpBtn = this.add.text(w / 2, h * 0.62, "How to Play", {
      ...base,
      fontSize: "48px",
      color: "#aaaaaa",
    });
    helpBtn.setOrigin(0.5, 0.5);
    helpBtn.setScrollFactor(0);
    helpBtn.setDepth(200);
    helpBtn.setInteractive({ useHandCursor: true });
    helpBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.showHelpModal();
    });

    this.modeSelectContainer = this.add.container(0, 0, [scoreBtnContainer, freeBtnContainer, helpBtn]);
    this.modeSelectContainer.setDepth(200);
    
    // モード選択画面の準備が完了したことを通知
    this.events.emit("modeSelectReady");
  }

  private hideModeSelect() {
    if (this.modeSelectContainer) {
      this.modeSelectContainer.destroy(true);
      this.modeSelectContainer = undefined;
    }
  }

  private onModeSelected(mode: GameMode) {
    // 【FREE MODE非アクティブ化】FREE MODEの場合はサイズ選択画面を表示
    // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
    if (mode === "free") {
      // FREE MODEは非アクティブ化されているため、処理をスキップ
      // 復元時は以下のコメントを解除
      /*
      // 保存データをチェック
      this.api?.checkSavedData((hasData) => {
        if (hasData) {
          // 保存データがある場合はContinue/New Game選択画面を表示
          this.showContinueConfirm();
        } else {
          // 保存データがない場合はサイズ選択画面を表示
          this.showPuzzleSizeSelect();
        }
      });
      return;
      */
      return; // FREE MODE非アクティブ化により早期リターン
    }

    // GAME PLAY（旧SCORE MODE）の場合は通常通り開始
    // game_start()をGAME PLAYボタンタップ時に発火
    if (mode === "score") {
      // UIの更新は先に行う
      this.logoImage.setVisible(false);
      this.hideModeSelect();
      this.showLoading();
      
      // this.apiが設定されるまで待ってからgame_start()を発火
      // （GameScene.create()でbindApi()が50ms後に実行されるため）
      const waitForApiAndStart = () => {
        console.log("[UIScene] waitForApiAndStartが実行されました。this.api:", this.api ? "設定済み" : "未設定");
        if (this.api) {
          // this.apiが設定されている場合は即座にgame_start()を発火
          console.log("[UIScene] this.apiが設定済みです。game_start()を呼び出します。");
          GameBox.start(() => {
            console.log("[UIScene] game_start()のコールバックが実行されました。selectModeを呼び出します。this.api:", this.api ? "設定済み" : "未設定");
            if (this.api) {
              this.api.selectMode(mode);
            } else {
              console.error("[UIScene] game_start()のコールバック内でthis.apiが未設定です。");
            }
          });
        } else {
          // this.apiがまだ設定されていない場合は少し待って再試行
          console.log("[UIScene] this.apiが未設定です。再試行します。");
          let retryCount = 0;
          const maxRetries = 20; // 最大1秒待機（50ms × 20回）
          const checkApi = () => {
            if (this.api) {
              console.log("[UIScene] this.apiが設定されました。game_start()を呼び出します。");
              GameBox.start(() => {
                console.log("[UIScene] game_start()のコールバックが実行されました。selectModeを呼び出します。this.api:", this.api ? "設定済み" : "未設定");
                if (this.api) {
                  this.api.selectMode(mode);
                } else {
                  console.error("[UIScene] game_start()のコールバック内でthis.apiが未設定です。");
                }
              });
            } else {
              retryCount++;
              if (retryCount < maxRetries) {
                console.log(`[UIScene] this.apiがまだ設定されていません。再試行します (${retryCount}/${maxRetries})`);
                this.time.delayedCall(50, checkApi);
              } else {
                console.error("[UIScene] this.apiが設定されませんでした。タイムアウトしました。");
                // タイムアウトした場合でも、game_start()を発火して続行を試みる
                GameBox.start(() => {
                  console.log("[UIScene] タイムアウト後のgame_start()コールバックが実行されました。");
                  const gameScene = this.scene.get("GameScene");
                  if (gameScene && (gameScene as any).selectMode) {
                    console.log("[UIScene] GameSceneから直接selectModeを呼び出します。");
                    (gameScene as any).selectMode(mode);
                  } else {
                    console.error("[UIScene] GameSceneが見つからないか、selectModeが存在しません。");
                  }
                });
              }
            }
          };
          checkApi();
        }
      };
      
      // 少し待ってから開始（bindApi()が実行される時間を確保）
      console.log("[UIScene] 100ms後にwaitForApiAndStartを実行します。");
      this.time.delayedCall(100, waitForApiAndStart);
    } else {
      this.logoImage.setVisible(false);
      this.hideModeSelect();
      this.showLoading();
      this.api?.selectMode(mode);
    }
  }

  /** 【FREE MODE非アクティブ化】FREE MODE用: パズルサイズ選択画面を表示 */
  // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
  private showPuzzleSizeSelect() {
    // FREE MODEは非アクティブ化されているため、処理をスキップ
    // 復元時は以下のコメントを解除
    return; // 早期リターン（復元時はこの行を削除）
    
    /* 復元時は以下のコメントを解除
    this.hidePuzzleSizeSelect();
    this.hideLoading(); // ローディングを非表示にする

    const w = this.scale.width;
    const h = this.scale.height;

      // オーバーレイ
      const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.8);
      overlay.setScrollFactor(0);
      overlay.setDepth(250);

      // タイトル
      const titleText = this.add.text(w / 2, h * 0.25, "パズルサイズを選択", {
        fontFamily: "PottaOne",
        fontSize: "48px",
        color: "#ffffff",
        align: "center",
      });
      titleText.setFontFamily("PottaOne");
      titleText.setOrigin(0.5, 0.5);
      titleText.setScrollFactor(0);
      titleText.setDepth(251);

      // 全クリデータは保存しないため、王冠アイコンは表示しない
      const sizes: Array<{ size: 2 | 3 | 4 | 5 | 6; label: string; color: string }> = [
        { size: 2, label: "2×2", color: "#00ff88" },
        { size: 3, label: "3×3", color: "#00aaff" },
        { size: 4, label: "4×4", color: "#ffaa00" },
        { size: 5, label: "5×5", color: "#ff6666" },
        { size: 6, label: "6×6", color: "#aa66ff" },
      ];

      const startY = h * 0.35;
      const buttonHeight = 70;
      const buttonGap = 20;
      const elements: Phaser.GameObjects.GameObject[] = [overlay, titleText];

      sizes.forEach((item, index) => {
        const y = startY + index * (buttonHeight + buttonGap);
        
        const sizeBtn = this.add.text(w / 2, y, item.label, {
          fontFamily: "PottaOne",
          fontSize: "56px",
          color: item.color,
          align: "center",
        });
        sizeBtn.setFontFamily("PottaOne");
        sizeBtn.setOrigin(0.5, 0.5);
        sizeBtn.setScrollFactor(0);
        sizeBtn.setDepth(251);
        sizeBtn.setInteractive({ useHandCursor: true });
        sizeBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
          pointer.event?.stopPropagation();
          this.playButtonSfx();
          this.logoImage.setVisible(false);
          this.hidePuzzleSizeSelect();
          this.hideModeSelect();
          this.showLoading();
          this.api?.selectMode("free");
          this.api?.selectPuzzleSize(item.size);
        });
        elements.push(sizeBtn);
      });

      // Topへ戻るボタン（6×6の下に余白をとって追加）
      const backToTopY = startY + sizes.length * (buttonHeight + buttonGap) + 40; // 6×6の下に40pxの余白
      const backToTopBtn = this.add.text(w / 2, backToTopY, "Topへ戻る", {
        fontFamily: "PottaOne",
        fontSize: "48px",
        color: "#aaaaaa",
        align: "center",
      });
      backToTopBtn.setFontFamily("PottaOne");
      backToTopBtn.setOrigin(0.5, 0.5);
      backToTopBtn.setScrollFactor(0);
      backToTopBtn.setDepth(251);
      backToTopBtn.setInteractive({ useHandCursor: true });
      backToTopBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
        pointer.event?.stopPropagation();
        this.playButtonSfx();
        this.hidePuzzleSizeSelect();
        this.showModeSelect();
      });
      elements.push(backToTopBtn);

      this.disableAllButtons();
      this.puzzleSizeSelectContainer = this.add.container(0, 0, elements);
      this.puzzleSizeSelectContainer.setDepth(250);
    */ // 復元時はこのコメントを解除
  }

  /** パズルサイズ選択画面を非表示 */
  private hidePuzzleSizeSelect() {
    if (this.puzzleSizeSelectContainer) {
      this.puzzleSizeSelectContainer.destroy(true);
      this.puzzleSizeSelectContainer = undefined;
    }
    this.enableAllButtons();
  }


  /** 全てのボタンを非アクティブ化（モーダル表示中） */
  private disableAllButtons() {
    // Muteボタン
    if (this.muteText && this.muteText.input) {
      this.muteText.removeInteractive();
    }
    // Pauseボタン
    if (this.pauseText && this.pauseText.input) {
      this.pauseText.removeInteractive();
    }
    // Debugボタン
    if (this.debugText && this.debugText.input) {
      this.debugText.removeInteractive();
    }
    // Startボタンはモーダル表示時には表示されていないので無効化不要
    // Mode選択ボタン（子要素を個別に無効化）
    if (this.modeSelectContainer) {
      const children = this.modeSelectContainer.list as Phaser.GameObjects.GameObject[];
      children.forEach((child) => {
        if (child instanceof Phaser.GameObjects.Container && child.input) {
          child.removeInteractive();
        }
      });
    }
  }

  /** 全てのボタンを再有効化（モーダル非表示時） */
  private enableAllButtons() {
    // Muteボタンは常に有効
    if (this.muteText && !this.muteText.input) {
      this.muteText.setInteractive({ useHandCursor: true });
    }
    // Pauseボタンはゲーム開始時のみ有効
    if (this.pauseText && this.gameStarted && !this.pauseText.input) {
      this.pauseText.setInteractive({ useHandCursor: true });
    }
    // Previewボタンはゲーム開始時のみ有効
    if (this.previewText && this.gameStarted && !this.previewText.input) {
      this.previewText.setInteractive({ useHandCursor: true });
    }
    // Debugボタンは非アクティブ（削除はしない）
    // if (this.debugText && this.gameStarted && !this.debugText.input) {
    //   this.debugText.setInteractive({ useHandCursor: true });
    // }
    // Startボタンは表示されている時のみ有効（setSizeが設定されているので問題なし）
    if (this.startButton && this.startButton.visible && !this.startButton.input) {
      this.startButton.setInteractive({ useHandCursor: true });
    }
    // Mode選択ボタンは表示されている時のみ有効（子要素を個別に再有効化）
    if (this.modeSelectContainer) {
      const children = this.modeSelectContainer.list as Phaser.GameObjects.GameObject[];
      children.forEach((child) => {
        if (child instanceof Phaser.GameObjects.Container && !child.input) {
          child.setInteractive({ useHandCursor: true });
        }
      });
    }
  }


  private showHelpModal() {
    this.hideHelpModal();

    const w = this.scale.width;
    const h = this.scale.height;

    // 背景オーバーレイ（暗くする）
    const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.7);
    overlay.setScrollFactor(0);
    overlay.setDepth(300);
    overlay.setInteractive({ useHandCursor: false });
    // モーダル外をタップすると閉じる
    overlay.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.hideHelpModal();
    });

    // ウィンドウサイズ
    const windowWidth = Math.min(w * 0.85, 700);
    const windowHeight = h * 0.7;
    const windowX = w / 2;
    const windowY = h / 2;
    const cornerRadius = 20;

    // ウィンドウボックス（背景）
    const windowBg = this.add.graphics();
    windowBg.fillStyle(0x1a1a1a, 1);
    windowBg.fillRoundedRect(
      windowX - windowWidth / 2,
      windowY - windowHeight / 2,
      windowWidth,
      windowHeight,
      cornerRadius
    );
    windowBg.lineStyle(3, 0xffffff, 0.3);
    windowBg.strokeRoundedRect(
      windowX - windowWidth / 2,
      windowY - windowHeight / 2,
      windowWidth,
      windowHeight,
      cornerRadius
    );
    windowBg.setScrollFactor(0);
    windowBg.setDepth(301);
    
    // ウィンドウ内をタップしても閉じないようにする（透明なRectangleでカバー）
    const windowHitArea = this.add.rectangle(windowX, windowY, windowWidth, windowHeight, 0x000000, 0);
    windowHitArea.setScrollFactor(0);
    windowHitArea.setDepth(301);
    windowHitArea.setInteractive({ useHandCursor: false });
    windowHitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
    });

    // 「×」ボタン（右上）
    const closeBtnSize = 40;
    const closeBtnX = windowX + windowWidth / 2 - closeBtnSize / 2 - 10;
    const closeBtnY = windowY - windowHeight / 2 + closeBtnSize / 2 + 10;
    const closeBtn = this.add.text(closeBtnX, closeBtnY, "×", {
      fontFamily: "PottaOne",
      fontSize: "30px",
      color: "#ffffff",
    });
    closeBtn.setFontFamily("PottaOne");
    closeBtn.setOrigin(0.5, 0.5);
    closeBtn.setScrollFactor(0);
    closeBtn.setDepth(302);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.hideHelpModal();
    });

    // タイトル
    const titleText = this.add.text(windowX, windowY - windowHeight / 2 + 60, "遊び方とルール", {
      fontFamily: "PottaOne",
      fontSize: "48px",
      color: "#ffffff",
      align: "center",
    });
    titleText.setFontFamily("PottaOne");
    titleText.setOrigin(0.5, 0.5);
    titleText.setScrollFactor(0);
    titleText.setDepth(302);
    titleText.setInteractive({ useHandCursor: false });
    titleText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
    });

    // ルールテキスト
    const rules = [
      "・空白マスに隣接するタイルを\nタップして移動",
      "",
      "・全てのタイルを正しい位置に\n配置するとクリア",
      "",
      "【Score Mode】",
      "",
      "・180秒の制限時間内にクリア",
      "",
      "・全20ステージ",
      "",
      // 【FREE MODE非アクティブ化】Free Modeに関する説明
      // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
      // 復元時は以下のコメントを解除
      /*
      "【Free Mode】",
      "",
      "・制限時間なし",
      "",
      "・パズルサイズを選択\n（2×2、3×3、4×4、5×5、6×6）",
      "",
      "・各サイズで全60ステージ",
      "",
      "・セーブ機能あり",
      "",
      */
    ];

    const lineHeight = 42;
    const emptyLineHeight = 20;
    const multilineExtraHeight = 20; // 改行を含むテキストの追加スペース
    const startY = windowY - windowHeight / 2 + 160; // タイトルとの余白をさらに増やす
    const rulesTexts: Phaser.GameObjects.Text[] = [];

    let currentY = startY;
    rules.forEach((rule) => {
      if (rule === "") {
        // 空行の場合はスペースを追加
        currentY += emptyLineHeight;
        return;
      }
      
      const ruleText = this.add.text(windowX, currentY, rule, {
        fontFamily: "PottaOne",
        fontSize: rule.startsWith("【") ? "32px" : "28px",
        color: rule.startsWith("【") ? "#ffaa00" : "#ffffff",
        align: "center",
      });
      ruleText.setFontFamily("PottaOne");
      ruleText.setOrigin(0.5, 0.5);
      ruleText.setScrollFactor(0);
      ruleText.setDepth(302);
      ruleText.setInteractive({ useHandCursor: false });
      ruleText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
        pointer.event?.stopPropagation();
      });
      rulesTexts.push(ruleText);
      
      // 改行を含むテキストの場合は追加のスペースを取る
      if (rule.includes("\n")) {
        currentY += lineHeight + multilineExtraHeight;
      } else {
        currentY += lineHeight;
      }
    });

    // 全てのボタンを非アクティブ化
    this.disableAllButtons();

    this.helpModalContainer = this.add.container(0, 0, [overlay, windowBg, windowHitArea, closeBtn, titleText, ...rulesTexts]);
    this.helpModalContainer.setDepth(300);
  }

  private hideHelpModal() {
    if (this.helpModalContainer) {
      this.helpModalContainer.destroy(true);
      this.helpModalContainer = undefined;
    }
    // 全てのボタンを再有効化
    this.enableAllButtons();
  }

  private showLoading() {
    this.hideLoading();

    const w = this.scale.width;
    const h = this.scale.height;

    // 背景オーバーレイ（暗くする）
    const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.8);
    overlay.setScrollFactor(0);
    overlay.setDepth(400);

    // フォントが読み込まれているか確認してからテキストを作成
    const createLoadingTexts = () => {
      // ローディングテキスト
      const loadingText = this.add.text(
        w / 2,
        h / 2 - 60,
        "Loading...",
        { fontFamily: "KirangHaerang", fontSize: "48px", color: "#ffffff" }
      );
      loadingText.setFontFamily("KirangHaerang");
      loadingText.setOrigin(0.5);
      loadingText.setScrollFactor(0);
      loadingText.setDepth(401);

      // 進捗バーの背景
      const barWidth = Math.min(w * 0.6, 400);
      const barHeight = 20;
      const barX = w / 2;
      const barY = h / 2 + 20;

      const progressBarBg = this.add.rectangle(barX, barY, barWidth, barHeight, 0x333333);
      progressBarBg.setOrigin(0.5, 0.5);
      progressBarBg.setScrollFactor(0);
      progressBarBg.setDepth(401);

      // 進捗バー
      const progressBar = this.add.rectangle(barX - barWidth / 2, barY, 0, barHeight, 0x00ff88);
      progressBar.setOrigin(0, 0.5);
      progressBar.setScrollFactor(0);
      progressBar.setDepth(401);

      // 進捗テキスト
      const progressText = this.add.text(
        barX,
        barY + 40,
        "0%",
        { fontFamily: "KirangHaerang", fontSize: "32px", color: "#ffffff" }
      );
      progressText.setFontFamily("KirangHaerang");
      progressText.setOrigin(0.5);
      progressText.setScrollFactor(0);
      progressText.setDepth(401);

      this.loadingContainer = this.add.container(0, 0, [overlay, loadingText, progressBarBg, progressBar, progressText]);
      this.loadingContainer.setDepth(400);
      
      // 進捗バーとテキストへの参照を保存（更新用）
      (this.loadingContainer as any).progressBar = progressBar;
      (this.loadingContainer as any).progressText = progressText;
      (this.loadingContainer as any).barWidth = barWidth;
      (this.loadingContainer as any).barX = barX;
    };

    // フォントが読み込まれているか確認
    const isFontLoaded = document.fonts.check('48px "KirangHaerang"');
    if (isFontLoaded) {
      createLoadingTexts();
    } else {
      // フォントが読み込まれていない場合は読み込んでから作成
      Promise.all([
        document.fonts.load('48px "KirangHaerang"'),
        document.fonts.load('32px "KirangHaerang"'),
      ]).then(() => {
        createLoadingTexts();
      });
    }
  }

  private updateLoadingProgress(progress: number) {
    if (!this.loadingContainer) return;
    
    const progressBar = (this.loadingContainer as any).progressBar as Phaser.GameObjects.Rectangle;
    const progressText = (this.loadingContainer as any).progressText as Phaser.GameObjects.Text;
    const barWidth = (this.loadingContainer as any).barWidth as number;
    const barX = (this.loadingContainer as any).barX as number;

    if (progressBar && progressText) {
      const progressWidth = barWidth * progress;
      progressBar.width = progressWidth;
      progressBar.x = barX - barWidth / 2;
      progressText.setText(`${Math.round(progress * 100)}%`);
    }
  }

  private hideLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.destroy(true);
      this.loadingContainer = undefined;
    }
  }

  /** ロゴのサイズを縦横比を保持して設定 */
  private updateLogoSize(targetWidth: number) {
    const logoTexture = this.textures.get("logo");
    const logoSource = logoTexture.getSourceImage() as HTMLImageElement;
    if (logoSource && logoSource.width && logoSource.height) {
      const aspectRatio = logoSource.width / logoSource.height;
      const logoHeight = targetWidth / aspectRatio;
      this.logoImage.setDisplaySize(targetWidth, logoHeight);
    }
  }

  private showCongratulations(p: {
    time: number;
    moves: number;
    stage?: number;
    isLast?: boolean;
    isTimeUp?: boolean;
    stageScore?: number;
    totalScore?: number;
    correctTiles?: number;
    gameMode?: GameMode;
  }) {
    this.hideCongratulations();
    
    // 全ステージクリア時とタイムアップ時の情報を保持
    this.isLastStage = p.isLast || false;
    this.lastTotalScore = p.totalScore || 0;
    this.isTimeUp = p.isTimeUp || false;

    // Stageクリア時はPauseボタンを無効化
    this.pauseText.setColor("#666666");
    this.pauseText.removeInteractive();
    this.previewText.setColor("#666666");
    this.previewText.removeInteractive();
    this.gameStarted = false; // イベントハンドラー内のチェックにも対応
    
    // Saveボタンをグレイアウト（Stageクリア時）
    const saveText = (this as any).saveText;
    if (saveText) {
      saveText.setColor("#666666");
      saveText.removeInteractive();
    }

    const w = this.scale.width;
    const h = this.scale.height;
    const base = this.game.registry.get("defaultTextStyle") || {
      fontFamily: "KirangHaerang",
      fontSize: "48px",
      color: "#ffffff",
    };

    const elements: Phaser.GameObjects.GameObject[] = [];

    // Lottieアニメーション - クリア時に再生（タイムアップ以外）
    if (!p.isTimeUp) {
      this.playLottie();
    }

    // Time Upの場合はGame Over表示
    if (p.isTimeUp) {
      const gameOverText = this.add.text(w / 2, h * 0.82, "GAME OVER", {
        ...base,
        fontSize: "72px",
        color: "#ff4444",
      });
      gameOverText.setOrigin(0.5, 0.5);
      elements.push(gameOverText);

      const timeUpText = this.add.text(w / 2, h * 0.865, "TIME UP!", {
        ...base,
        fontSize: "48px",
        color: "#ffffff",
      });
      timeUpText.setOrigin(0.5, 0.5);
      elements.push(timeUpText);

      // スコア表示
      if (p.gameMode === "score") {
        const scoreText = this.add.text(w / 2, h * 0.91, `TOTAL SCORE: ${p.totalScore}`, {
          ...base,
          fontSize: "44px",
          color: "#ffdd00",
        });
        scoreText.setOrigin(0.5, 0.5);
        elements.push(scoreText);
      }

      // Back to Topボタン
      const backBtn = this.add.text(w / 2, h * 0.955, "◀ Back to Top", {
        ...base,
        fontSize: "48px",
        color: "#00aaff",
      });
      backBtn.setOrigin(0.5, 0.5);
      backBtn.setInteractive({ useHandCursor: true });
      backBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
        pointer.event?.stopPropagation();
        this.playButtonSfx();
        this.onBackToTop();
      });
      elements.push(backBtn);
    } else {
      // 通常のクリア表示
      const titleMsg = p.isLast ? "ALL CLEAR!" : `Stage ${p.stage} Clear!`;
      const congratsText = this.add.text(w / 2, h * 0.82, titleMsg, {
        ...base,
        fontSize: p.isLast ? "72px" : "56px",
        color: "#ffdd00",
      });
      congratsText.setOrigin(0.5, 0.5);
      elements.push(congratsText);

      // 結果表示
      const resultText = this.add.text(w / 2, h * 0.87, `TIME: ${p.time.toFixed(2)}s   MOVES: ${p.moves}`, {
        ...base,
        fontSize: "40px",
      });
      resultText.setOrigin(0.5, 0.5);
      elements.push(resultText);

      // スコアモードの場合はスコア表示
      if (p.gameMode === "score") {
        const scoreMsg = p.isLast
          ? `TOTAL SCORE: ${p.totalScore}`
          : `+${p.stageScore} pts (Total: ${p.totalScore})`;
        const scoreText = this.add.text(w / 2, h * 0.92, scoreMsg, {
          ...base,
          fontSize: "44px",
          color: "#ffdd00",
        });
        scoreText.setOrigin(0.5, 0.5);
        elements.push(scoreText);
      }

      // 次ステージへのボタン or Back to Top
      if (!p.isLast) {
        this.nextStageButton = this.add.text(w / 2, h * 0.97, "▶ Next Stage", {
          ...base,
          fontSize: "48px",
          color: "#00ff88",
        });
        this.nextStageButton.setOrigin(0.5, 0.5);
        this.nextStageButton.setInteractive({ useHandCursor: true });
        this.nextStageButton.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
          pointer.event?.stopPropagation();
          this.playButtonSfx();
          this.onNextStagePressed();
        });
        elements.push(this.nextStageButton);
      } else {
        // ALL CLEAR時もBack to Topボタン
        const backBtn = this.add.text(w / 2, h * 0.97, "◀ Back to Top", {
          ...base,
          fontSize: "48px",
          color: "#00aaff",
        });
        backBtn.setOrigin(0.5, 0.5);
        backBtn.setInteractive({ useHandCursor: true });
        backBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
          pointer.event?.stopPropagation();
          this.playButtonSfx();
          this.onBackToTop();
        });
        elements.push(backBtn);
      }
    }

    this.congratsContainer = this.add.container(0, 0, elements);
    this.congratsContainer.setDepth(200);
    this.congratsContainer.setAlpha(0);

    this.tweens.add({
      targets: this.congratsContainer,
      alpha: 1,
      duration: 500,
      ease: "Quad.easeOut",
    });
  }

  private onBackToTop() {
    this.hideCongratulations();
    
    // Score Modeの場合、Time、Score、Stageをリセット
    const isScoreMode = this.api?.getGameMode() === "score";
    if (isScoreMode) {
      // Timeリセット（0.00に、フォント色も含め）
      this.timeText.setText("TIME 0.00");
      this.timeText.setColor("#ffffff");
      // Score非表示
      this.scoreText.setText("");
      // Stageリセット（ステータスを非表示）
      if (this.statusText) {
        this.statusText.setText("");
      }
    }
    
    // タイムアップ時（Score Modeのゲームオーバー）は既にgame_endが呼ばれているため、
    // ここではrestart()のみを呼ぶ
    if (this.isTimeUp && this.lastTotalScore !== undefined) {
      // タイムアップ時は既にgame_endが呼ばれているため、restart()のみを呼ぶ
      try {
        this.api?.restart();
      } catch (e) {
        console.error("[UIScene] onBackToTop - restartエラー:", e);
        // エラー時でもrestart()を試みる
        this.api?.restart();
      }
      // フラグをリセット
      this.isLastStage = false;
      this.lastTotalScore = undefined;
      this.isTimeUp = false;
    } else if (this.isLastStage && this.api?.getGameMode() === "score") {
      // Score Modeの全クリ時はgame_endを呼ぶ（Free Modeと同様）
      try {
        GameBox.end(this.lastTotalScore || 0, () => {
          try {
            // game_endのコールバック内でrestart()を呼ぶ
            this.api?.restart();
          } catch (e) {
            console.error("[UIScene] onBackToTop - restartエラー:", e);
            // エラー時でもrestart()を試みる
            this.api?.restart();
          }
        });
      } catch (e) {
        console.error("[UIScene] onBackToTop - GameBox.endエラー:", e);
        // エラー時でもrestart()を実行してゲームを続行できるようにする
        this.api?.restart();
      }
      // フラグをリセット
      this.isLastStage = false;
      this.lastTotalScore = undefined;
      this.isTimeUp = false;
    // 【FREE MODE非アクティブ化】Free Modeの全クリ時の処理
    // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
    // 復元時は以下のコメントを解除
    /*
    } else if (this.isLastStage && this.api?.getGameMode() === "free") {
      // Free Modeの全クリ時は進捗を保存して終了
      try {
        this.api.saveProgressAndEnd(() => {
          try {
            // 保存完了後、restart()を呼ぶ
            this.api?.restart();
          } catch (e) {
            console.error("[UIScene] onBackToTop - restartエラー:", e);
            this.api?.restart();
          }
        });
      } catch (e) {
        console.error("[UIScene] onBackToTop - saveProgressAndEndエラー:", e);
        this.api?.restart();
      }
      // フラグをリセット
      this.isLastStage = false;
      this.lastTotalScore = undefined;
      this.isTimeUp = false;
    */
    } else {
      // 通常の場合はそのままrestart()
      try {
        this.api?.restart();
      } catch (e) {
        console.error("[UIScene] onBackToTop - restartエラー:", e);
        // エラー時でもrestart()を試みる
        this.api?.restart();
      }
      // フラグをリセット
      this.isLastStage = false;
      this.lastTotalScore = undefined;
      this.isTimeUp = false;
    }
  }

  private onNextStagePressed() {
    this.hideCongratulations();
    this.api?.nextStage();
    this.startButton.setVisible(true);
    this.startArrow.setAlpha(1);
    this.arrowTween?.resume();
    this.gameStarted = false;
    this.pauseText.setColor("#666666");
    this.pauseText.removeInteractive();
    this.previewText.setColor("#666666");
    this.previewText.removeInteractive();
    this.debugText.setColor("#666666");
    
  }

  private playLottie() {
    this.stopLottie();

    // DOMが準備できているか確認
    if (typeof document === "undefined" || !document.createElement) {
      console.error("[UIScene] playLottie: document not available");
      return;
    }

    // Phaserのキャンバス要素を取得
    const canvas = this.game.canvas;
    if (!canvas) {
      console.error("[UIScene] playLottie: Phaser canvas not found");
      return;
    }

    // キャンバスの位置とサイズを取得
    const canvasRect = canvas.getBoundingClientRect();
    const canvasWidth = canvasRect.width;
    const canvasHeight = canvasRect.height;
    const canvasLeft = canvasRect.left;
    const canvasTop = canvasRect.top;

    // 親要素を取得（body）
    let parentElement: HTMLElement | null = null;
    try {
      parentElement = document.body;
    } catch (e) {
      console.error("[UIScene] playLottie: failed to get parent element", e);
      return;
    }

    if (!parentElement) {
      console.error("[UIScene] playLottie: parent element not found");
      return;
    }

    // コンテナを作成
    let container: HTMLDivElement | null = null;
    try {
      container = document.createElement("div");
    } catch (e) {
      console.error("[UIScene] playLottie: failed to create element", e);
      return;
    }

    if (!container || !container.style) {
      console.error("[UIScene] playLottie: container or style not available");
      return;
    }
    
    try {
      const style = container.style;
      if (!style) {
        console.error("[UIScene] playLottie: style object is null");
        return;
      }
      // キャンバスの位置とサイズに合わせて配置
      style.position = "fixed";
      style.left = `${canvasLeft}px`;
      style.top = `${canvasTop}px`;
      style.width = `${canvasWidth}px`;
      style.height = `${canvasHeight}px`;
      style.pointerEvents = "none";
      style.zIndex = "1000";
      style.transform = "scale(1.3)";
      style.transformOrigin = "center center";
      style.overflow = "hidden";
    } catch (e) {
      console.error("[UIScene] playLottie: failed to set styles", e);
      return;
    }

    // 親要素に追加
    try {
      parentElement.appendChild(container);
      this.lottieContainer = container;
    } catch (e) {
      console.error("[UIScene] playLottie: failed to append container", e);
      return;
    }

    // Lottieアニメーションを読み込み
    if (!this.lottieContainer) {
      console.error("[UIScene] playLottie: lottieContainer is null");
      return;
    }

    try {
      this.lottieAnim = lottie.loadAnimation({
        container: this.lottieContainer,
        renderer: "svg",
        loop: false,
        autoplay: true,
        path: "assets/lottie/congratulations.json",
      });
    } catch (e) {
      console.error("[UIScene] playLottie: failed to load animation", e);
      this.stopLottie();
      return;
    }

    if (this.lottieAnim) {
      this.lottieAnim.addEventListener("complete", () => {
        this.stopLottie();
      });
    }
  }

  private updateLottieContainerPosition() {
    if (!this.lottieContainer) return;
    
    const canvas = this.game.canvas;
    if (!canvas) return;
    
    const canvasRect = canvas.getBoundingClientRect();
    const style = this.lottieContainer.style;
    if (style) {
      style.left = `${canvasRect.left}px`;
      style.top = `${canvasRect.top}px`;
      style.width = `${canvasRect.width}px`;
      style.height = `${canvasRect.height}px`;
    }
  }

  private stopLottie() {
    if (this.lottieAnim) {
      this.lottieAnim.destroy();
      this.lottieAnim = undefined;
    }
    if (this.lottieContainer) {
      this.lottieContainer.remove();
      this.lottieContainer = undefined;
    }
  }

  private hideCongratulations() {
    if (this.congratsContainer) {
      this.congratsContainer.destroy(true);
      this.congratsContainer = undefined;
    }
    if (this.nextStageButton) {
      this.nextStageButton.destroy();
      this.nextStageButton = undefined;
    }
    this.stopLottie();
  }

  private playButtonSfx() {
    if (this.api?.getMuted()) return;
    this.sound.play("sfxButton", { volume: 0.6 });
  }

  private bindButtons() {
    this.bound = true;
    this.muteText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      if (!this.api) return;
      this.playButtonSfx();
      this.api.setMuted(!this.api.getMuted());
      this.refreshMuteLabel();
    });

    this.pauseText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      if (!this.api || !this.gameStarted) return;
      this.playButtonSfx();
      if (this.statusText.text === "Paused") {
        this.api.resume();
        this.pauseText.setText("PAUSE");
      } else {
        this.api.pause();
        this.pauseText.setText("RESUME");
      }
    });

    this.previewText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      if (!this.api || !this.gameStarted) return;
      this.playButtonSfx();
      this.api.togglePreview();
    });


    // Debugボタン: シングルクリックで即座にクリア、ダブルクリックで最後のステージに設定
    let debugClickTimer: ReturnType<typeof setTimeout> | null = null;
    this.debugText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      if (!this.gameStarted) return;
      
      if (debugClickTimer === null) {
        // シングルクリック: 即座にクリア
        debugClickTimer = setTimeout(() => {
          this.playButtonSfx();
          this.api?.solve();
          debugClickTimer = null;
        }, 300); // 300ms以内に2回クリックされなければシングルクリック
      } else {
        // ダブルクリック: 最後のステージに設定して即座にクリア
        clearTimeout(debugClickTimer);
        debugClickTimer = null;
        this.playButtonSfx();
        this.api?.setToLastStage();
        // 最後のステージに設定後、即座にクリアして全ステージクリア状態をテスト
        this.time.delayedCall(100, () => {
          this.api?.solve();
        });
      }
    });


    this.startButton.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.api?.start();
      this.gameStarted = true;
      this.startButton.setVisible(false);
      this.arrowTween?.pause();
      this.pauseText.setColor("#ffffff");
      this.pauseText.setInteractive({ useHandCursor: true });
      this.previewText.setColor("#ffffff");
      this.previewText.setInteractive({ useHandCursor: true });
      // Debugボタンは非アクティブ（削除はしない）
      // this.debugText.setColor("#ff6666");
      // this.debugText.setInteractive({ useHandCursor: true });
      // 【FREE MODE非アクティブ化】Saveボタンを有効化（Free Modeの場合）
      // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
      // 復元時は以下のコメントを解除
      /*
      const saveText = (this as any).saveText;
      if (saveText && this.api?.getGameMode() === "free") {
        saveText.setVisible(true);
        saveText.setColor("#ffffff");
        saveText.setInteractive({ useHandCursor: true });
      }
      */
      // Time 3秒ボタンは非アクティブ（削除はしない）
      // if (this.time3SecText && this.api?.getGameMode() === "score") {
      //   this.time3SecText.setVisible(true);
      //   this.time3SecText.setColor("#ffaa00");
      //   this.time3SecText.setInteractive({ useHandCursor: true });
      // }
    });
  }

  private refreshMuteLabel() {
    if (!this.api) return;
    this.muteText.setText(`MUTE: ${this.api.getMuted() ? "ON" : "OFF"}`);
  }

  /** Continue確認モーダルを表示 */
  // @ts-expect-error - FREE MODE非アクティブ化により未使用（復元時に使用）
  private showContinueConfirm() {
    this.hideContinueConfirm();

    const w = this.scale.width;
    const h = this.scale.height;

    // 背景オーバーレイ（暗くする）
    const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.7);
    overlay.setScrollFactor(0);
    overlay.setDepth(300);
    overlay.setInteractive({ useHandCursor: false });
    // モーダル外をタップすると閉じる
    overlay.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.hideContinueConfirm();
    });

    // ウィンドウサイズ（Saveモーダルに合わせる）
    const windowWidth = Math.min(w * 0.9, 680);
    const windowHeight = h * 0.45;
    const windowX = w / 2;
    const windowY = h / 2;
    const cornerRadius = 20;

    // ウィンドウボックス（背景）
    const windowBg = this.add.graphics();
    windowBg.fillStyle(0x1a1a1a, 1);
    windowBg.fillRoundedRect(
      windowX - windowWidth / 2,
      windowY - windowHeight / 2,
      windowWidth,
      windowHeight,
      cornerRadius
    );
    windowBg.lineStyle(3, 0xffffff, 0.3);
    windowBg.strokeRoundedRect(
      windowX - windowWidth / 2,
      windowY - windowHeight / 2,
      windowWidth,
      windowHeight,
      cornerRadius
    );
    windowBg.setScrollFactor(0);
    windowBg.setDepth(301);
    
    // ウィンドウ内をタップしても閉じないようにする（透明なRectangleでカバー）
    const windowHitArea = this.add.rectangle(windowX, windowY, windowWidth, windowHeight, 0x000000, 0);
    windowHitArea.setScrollFactor(0);
    windowHitArea.setDepth(301);
    windowHitArea.setInteractive({ useHandCursor: false });
    windowHitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
    });

    // 「×」ボタン（右上）
    const closeBtnSize = 40;
    const closeBtnX = windowX + windowWidth / 2 - closeBtnSize / 2 - 10;
    const closeBtnY = windowY - windowHeight / 2 + closeBtnSize / 2 + 10;
    const closeBtn = this.add.text(closeBtnX, closeBtnY, "×", {
      fontFamily: "PottaOne",
      fontSize: "30px",
      color: "#ffffff",
    });
    closeBtn.setFontFamily("PottaOne");
    closeBtn.setOrigin(0.5, 0.5);
    closeBtn.setScrollFactor(0);
    closeBtn.setDepth(302);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.hideContinueConfirm();
    });

    // タイトル（Saveモーダルに合わせて配置）
    const titleText = this.add.text(windowX, windowY - 70, "続きから始めますか？", {
      fontFamily: "PottaOne",
      fontSize: "48px",
      color: "#ffffff",
      align: "center",
    });
    titleText.setFontFamily("PottaOne");
    titleText.setOrigin(0.5, 0.5);
    titleText.setScrollFactor(0);
    titleText.setDepth(302);
    titleText.setInteractive({ useHandCursor: false });
    titleText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
    });

    // New Gameボタン（左側に配置）
    const newGameBtn = this.add.text(windowX - 120, windowY + 70, "最初から", {
      fontFamily: "PottaOne",
      fontSize: "48px",
      color: "#ff8800",
      align: "center",
    });
    newGameBtn.setFontFamily("PottaOne");
    newGameBtn.setOrigin(0.5, 0.5);
    newGameBtn.setScrollFactor(0);
    newGameBtn.setDepth(302);
    newGameBtn.setInteractive({ useHandCursor: true });
    newGameBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.hideContinueConfirm();
      this.showPuzzleSizeSelect();
    });

    // Continueボタン（右側に配置）
    const continueBtn = this.add.text(windowX + 120, windowY + 70, "続きから", {
      fontFamily: "PottaOne",
      fontSize: "48px",
      color: "#00aaff",
      align: "center",
    });
    continueBtn.setFontFamily("PottaOne");
    continueBtn.setOrigin(0.5, 0.5);
    continueBtn.setScrollFactor(0);
    continueBtn.setDepth(302);
    continueBtn.setInteractive({ useHandCursor: true });
    continueBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.hideContinueConfirm();
      this.logoImage.setVisible(false);
      this.hideModeSelect();
      this.showLoading();
      this.api?.continueFromSaved();
    });

    this.disableAllButtons();
    const continueConfirmContainer = this.add.container(0, 0, [overlay, windowBg, windowHitArea, closeBtn, titleText, newGameBtn, continueBtn]);
    continueConfirmContainer.setDepth(300);
    (this as any).continueConfirmContainer = continueConfirmContainer;
  }

  /** Continue確認モーダルを非表示 */
  private hideContinueConfirm() {
    const container = (this as any).continueConfirmContainer;
    if (container) {
      container.destroy(true);
      (this as any).continueConfirmContainer = undefined;
    }
    this.enableAllButtons();
  }

  /** Save確認モーダルを表示 */
  // @ts-expect-error - FREE MODE非アクティブ化により未使用（復元時に使用）
  private showSaveConfirm(_onYes: () => void) {
    this.hideSaveConfirm();

    const w = this.scale.width;
    const h = this.scale.height;

    // 背景オーバーレイ（暗くする）
    const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.7);
    overlay.setScrollFactor(0);
    overlay.setDepth(300);
    overlay.setInteractive({ useHandCursor: false });
    // モーダル外をタップすると閉じる
    overlay.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.hideSaveConfirm();
      this.api?.resume();
    });

    // ウィンドウサイズ
    const windowWidth = Math.min(w * 0.9, 680);
    const windowHeight = h * 0.45;
    const windowX = w / 2;
    const windowY = h / 2;
    const cornerRadius = 20;

    // ウィンドウボックス（背景）
    const windowBg = this.add.graphics();
    windowBg.fillStyle(0x1a1a1a, 1);
    windowBg.fillRoundedRect(
      windowX - windowWidth / 2,
      windowY - windowHeight / 2,
      windowWidth,
      windowHeight,
      cornerRadius
    );
    windowBg.lineStyle(3, 0xffffff, 0.3);
    windowBg.strokeRoundedRect(
      windowX - windowWidth / 2,
      windowY - windowHeight / 2,
      windowWidth,
      windowHeight,
      cornerRadius
    );
    windowBg.setScrollFactor(0);
    windowBg.setDepth(301);
    
    // ウィンドウ内をタップしても閉じないようにする（透明なRectangleでカバー）
    const windowHitArea = this.add.rectangle(windowX, windowY, windowWidth, windowHeight, 0x000000, 0);
    windowHitArea.setScrollFactor(0);
    windowHitArea.setDepth(301);
    windowHitArea.setInteractive({ useHandCursor: false });
    windowHitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
    });

    // 「×」ボタン（右上）
    const closeBtnSize = 40;
    const closeBtnX = windowX + windowWidth / 2 - closeBtnSize / 2 - 10;
    const closeBtnY = windowY - windowHeight / 2 + closeBtnSize / 2 + 10;
    const closeBtn = this.add.text(closeBtnX, closeBtnY, "×", {
      fontFamily: "PottaOne",
      fontSize: "30px",
      color: "#ffffff",
    });
    closeBtn.setFontFamily("PottaOne");
    closeBtn.setOrigin(0.5, 0.5);
    closeBtn.setScrollFactor(0);
    closeBtn.setDepth(302);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.hideSaveConfirm();
      this.api?.resume();
    });

    // タイトル（改行して表示、中央上部に配置）
    const titleText = this.add.text(windowX, windowY - 70, "セーブしてゲームを\n終了しますか？", {
      fontFamily: "PottaOne",
      fontSize: "48px",
      color: "#ffffff",
      align: "center",
    });
    titleText.setFontFamily("PottaOne");
    titleText.setOrigin(0.5, 0.5);
    titleText.setScrollFactor(0);
    titleText.setDepth(302);
    titleText.setInteractive({ useHandCursor: false });
    titleText.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
    });

    // Yesボタン（左側に配置）
    const yesBtn = this.add.text(windowX - 80, windowY + 70, "はい", {
      fontFamily: "PottaOne",
      fontSize: "48px",
      color: "#00ff88",
      align: "center",
    });
    yesBtn.setFontFamily("PottaOne");
    yesBtn.setOrigin(0.5, 0.5);
    yesBtn.setScrollFactor(0);
    yesBtn.setDepth(302);
    yesBtn.setInteractive({ useHandCursor: true });
    yesBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.hideSaveConfirm();
      _onYes();
    });

    // Noボタン（右側に配置）
    const noBtn = this.add.text(windowX + 80, windowY + 70, "いいえ", {
      fontFamily: "PottaOne",
      fontSize: "48px",
      color: "#ff6666",
      align: "center",
    });
    noBtn.setFontFamily("PottaOne");
    noBtn.setOrigin(0.5, 0.5);
    noBtn.setScrollFactor(0);
    noBtn.setDepth(302);
    noBtn.setInteractive({ useHandCursor: true });
    noBtn.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.playButtonSfx();
      this.hideSaveConfirm();
      // "いいえ"をタップしたらゲームを再開
      this.api?.resume();
    });

    this.disableAllButtons();
    const saveConfirmContainer = this.add.container(0, 0, [overlay, windowBg, windowHitArea, closeBtn, titleText, yesBtn, noBtn]);
    saveConfirmContainer.setDepth(300);
    (this as any).saveConfirmContainer = saveConfirmContainer;
  }

  /** Save確認モーダルを非表示 */
  private hideSaveConfirm() {
    const container = (this as any).saveConfirmContainer;
    if (container) {
      container.destroy(true);
      (this as any).saveConfirmContainer = undefined;
    }
    this.enableAllButtons();
  }
}
